import React, { useState, useEffect } from 'react';
import { InterviewPhase, PHASE_DISPLAY_NAMES } from '../types';
import { buildApiUrl } from '../config';
import axios from 'axios';

interface PhaseTrackerProps {
  interviewId: string | null;
  currentPhase: InterviewPhase;
}

interface CompletedPhasesResponse {
  completed_phases: string[];
  total_phases: number;
  interview_id: string;
}

const PhaseTracker: React.FC<PhaseTrackerProps> = ({ interviewId, currentPhase }) => {
  const [completedPhases, setCompletedPhases] = useState<string[]>([]);

  // Define all phases in order (excluding GET_STARTED)
  const allPhases = [
    InterviewPhase.INTRODUCTION_AND_BACKGROUND,
    InterviewPhase.METHODOLOGY_OVERVIEW,
    InterviewPhase.SCENARIO_AND_TACIT_KNOWLEDGE
  ];

  // Fetch completed phases only when currentPhase changes
  const fetchCompletedPhases = async () => {
    if (!interviewId) return;
    
    try {
      const response = await axios.get(buildApiUrl(`/api/interview/${interviewId}/completed-phases`));
      const data: CompletedPhasesResponse = response.data;
      setCompletedPhases(data.completed_phases);
    } catch (error) {
      console.error('Error fetching completed phases:', error);
    }
  };

  // Fetch completed phases when interviewId or currentPhase changes
  useEffect(() => {
    if (interviewId) {
      fetchCompletedPhases();
    }
  }, [interviewId, currentPhase]);

  // Debug: Log when currentPhase changes
  useEffect(() => {
   // console.log(`🔄 PhaseTracker: currentPhase changed to ${currentPhase}`);
  }, [currentPhase]);

  const getPhaseStatus = (phase: InterviewPhase) => {
    const phaseValue = phase.valueOf();
    
    // FIX: Use only the backend's current_phase to determine current phase
    // Don't rely on completed phases for current phase determination
    if (phase === currentPhase && currentPhase !== InterviewPhase.GET_STARTED) {
     // console.log(`🎯 PhaseTracker: Marking ${phaseValue} as CURRENT (from backend current_phase: ${currentPhase})`);
      return 'current';
    } else if (completedPhases.includes(phaseValue)) {
      //console.log(`✅ PhaseTracker: Marking ${phaseValue} as COMPLETED`);
      return 'completed';
    } else {
      //console.log(`⏳ PhaseTracker: Marking ${phaseValue} as PENDING`);
      return 'pending';
    }
  };

  const getPhaseNumber = (phase: InterviewPhase): number => {
    // Map phases to their display numbers (1-5, excluding GET_STARTED)
    const phaseNumberMap: Partial<Record<InterviewPhase, number>> = {
      [InterviewPhase.INTRODUCTION_AND_BACKGROUND]: 1,
      [InterviewPhase.METHODOLOGY_OVERVIEW]: 2,
      [InterviewPhase.SCENARIO_AND_TACIT_KNOWLEDGE]: 3
    };
    return phaseNumberMap[phase] || 1;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Interview Progress</h3>
      
      <div className="space-y-3">
        {allPhases.map((phase) => {
          const status = getPhaseStatus(phase);
          const phaseNumber = getPhaseNumber(phase);
          const displayName = PHASE_DISPLAY_NAMES[phase];
          
          return (
            <div
              key={phase}
              className={`flex items-center space-x-3 p-3 rounded-lg transition-all duration-200 ${
                status === 'completed'
                  ? 'bg-green-50 border border-green-200'
                  : status === 'current'
                  ? 'bg-blue-50 border border-blue-200'
                  : 'bg-gray-50 border border-gray-200'
              }`}
            >
              {/* Phase Number */}
              <div
                className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                  status === 'completed'
                    ? 'bg-green-500 text-white'
                    : status === 'current'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-300 text-gray-600'
                }`}
              >
                {phaseNumber}
              </div>
              
              {/* Phase Name */}
              <div className="flex-1 min-w-0">
                <div
                  className={`text-sm font-medium ${
                    status === 'completed'
                      ? 'text-green-800'
                      : status === 'current'
                      ? 'text-blue-800'
                      : 'text-gray-600'
                  }`}
                >
                  {displayName}
                </div>
              </div>
              
              {/* Status Icon */}
              <div className="flex-shrink-0">
                {status === 'completed' ? (
                  <svg
                    className="w-5 h-5 text-green-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M20 6L9 17l-5-5"
                    />
                  </svg>
                ) : status === 'current' ? (
                  <div className="w-5 h-5 flex items-center justify-center">
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                  </div>
                ) : (
                  <div className="w-5 h-5 flex items-center justify-center">
                    <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Summary */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="flex justify-between items-center text-sm text-gray-600">
          <span>Completed: {completedPhases.length}</span>
          <span>Total: {allPhases.length}</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
          <div
            className="bg-green-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${(completedPhases.length / allPhases.length) * 100}%` }}
          ></div>
        </div>
      </div>
    </div>
  );
};

export default PhaseTracker; 